import React from "react";
import Table from "../../components/Table";
import { Column } from "../../components/BaseTable";
import { HuidigeSituatiePensioen } from "../../../../.generated/rapportage/rapportagetypes";
import { bedragFormat } from "../../../../shared/utils/currency";
import { formatDate, formatPercentage } from "../../../formatters";

export interface SinglePensioenTableProps {
  data: HuidigeSituatiePensioen;
}

export interface PensioenTableProps {
  data: HuidigeSituatiePensioen[];
}

export const SinglePensioenTable: React.FC<SinglePensioenTableProps> = ({
  data,
}: SinglePensioenTableProps) => {
  const columns: Column<HuidigeSituatiePensioen>[] = [
    {
      header: data.titel ?? "",
      accessor: () => " ",
    },
    {
      header: "Pensioenuitvoerder",
      accessor: "pensioenuitvoerder",
    },
    {
      header: "Deelnemer pensioenregeling",
      accessor: "deelnemer",
    },
    {
      header: "Soort pensioenregeling",
      accessor: (row) => {
        const parts = [];
        if (row.soortPensioenregeling) {
          parts.push(row.soortPensioenregeling);
        }
        if (row.opbouwPercentage) {
          parts.push(`${formatPercentage(row.opbouwPercentage)}`);
        }
        return parts.join(", ");
      },
    },
    {
      header: "Pensioengevend salaris",
      accessor: "pensioengevendSalaris",
      dataType: "currency",
    },
    {
      header: "Franchise",
      accessor: "franchise",
      dataType: "currency",
    },
    {
      header: "Pensioengrondslag",
      accessor: "pensioengrondslag",
      dataType: "currency",
    },
    {
      header: "Pensioenaangroei (factor A)",
      accessor: (row) => {
        if (!row.pensioenaangroeiBedrag) return null;
        const datumText = formatDate(row.pensioenOverzichtDatum);
        return datumText
          ? `${bedragFormat(row.pensioenaangroeiBedrag, 0)}, ${datumText}`
          : bedragFormat(row.pensioenaangroeiBedrag, 0);
      },
    },
    {
      header: "Bijdrage pré-pensioen",
      accessor: "bijdragePrePensioen",
      dataType: "currency",
    },
    {
      header: "Bijdrage ouderdomspensioen",
      accessor: "bijdrageOuderdomsPensioen",
      dataType: "currency",
    },
    {
      header: "Vrijwillige eigen bijdrage",
      accessor: "bijdrageVrijwillig",
      dataType: "currency",
    },
    {
      header: "Prépensioen, ingangsdatum, leeftijd",
      accessor: (row) => {
        if (!row.prePensioenBedrag) return null;
        const datumText = formatDate(row.prePensioenIngangsdatum);
        const parts = [
          bedragFormat(row.prePensioenBedrag),
          datumText,
          row.prePensioenLeeftijd,
        ].filter(Boolean);
        return `€ ${parts.join(", ")}`;
      },
    },
    {
      header: "Ouderdomspensioen, ingangsdatum, leeftijd",
      accessor: (row) => {
        if (!row.ouderdomspensioenBedrag) return null;
        const datumText = formatDate(row.ouderdomspensioenIngangsdatum);
        const parts = [
          bedragFormat(row.ouderdomspensioenBedrag),
          datumText,
          row.ouderdomspensioenLeeftijd,
        ].filter(Boolean);
        return ` ${parts.join(", ")}`;
      },
    },
    {
      header: "Overbruggingspensioen",
      accessor: "overbruggingsPensioen",
      dataType: "currency",
    },
    {
      header: "Nabestaandenpensioen tot AOW bij overlijden voor AOW-leeftijd",
      accessor: "nabestaandenPensioenVoor65",
      dataType: "currency",
    },
    {
      header: "Nabestaandenpensioen na AOW bij overlijden voor AOW-leeftijd",
      accessor: "nabestaandenPensioenVoor65NaAow",
      dataType: "currency",
    },
    {
      header: "Nabestaandenpensioen tot AOW bij overlijden na AOW-leeftijd",
      accessor: "nabestaandenPensioenNa65TotAow",
      dataType: "currency",
    },
    {
      header: "Nabestaandenpensioen na AOW bij overlijden na AOW-leeftijd",
      accessor: "nabestaandenPensioenNa65",
      dataType: "currency",
    },
    {
      header: "Wezenpensioen, eindleeftijd",
      accessor: (row) => {
        if (!row.wezenpensioenBedrag) return null;
        const eindleeftijdText =
          row.wezenpensioenEindleeftijd?.toString() || "";
        const parts = [
          bedragFormat(row.wezenpensioenBedrag),
          `${eindleeftijdText} jaar`,
        ].filter(Boolean);
        return parts.join(", ");
      },
    },
    {
      header: "Invaliditeitspensioen",
      accessor: (row) => (row as any).invaliditeitspensioenBedrag,
      dataType: "currency",
    },
  ];

  return (
    <div className="mb-4">
      <Table
        columns={columns}
        data={[data]}
        rotate={true}
        showHeader={true}
        filterNullAnd0Columns={true}
        id={`pensioen-table-${data.titel?.replace(/\s+/g, "-").toLowerCase()}`}

      />
    </div>
  );
};

export const PensioenTable: React.FC<PensioenTableProps> = ({
  data,
}: PensioenTableProps) => {
  return (
    <div className="pensioenen-section">
      {data.map((pensioen, index) => (
        <SinglePensioenTable key={index} data={pensioen} />
      ))}
    </div>
  );
};
